import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";

const Wrap = styled.section`
  padding: 5rem 0;

  @media (min-width: 900px) { padding: 7rem 0; }
`;

const Inner = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 1.5rem;

  @media (min-width: 768px) { padding: 0 2rem; }
`;

const Head = styled.div`
  max-width: 720px;
  margin: 0 auto 3.5rem;
  text-align: center;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  font-family: var(--mono);
  font-size: 0.7rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: 1.25rem;
  font-weight: 500;
`;

const Title = styled.h2`
  font-family: var(--sans);
  font-weight: 700;
  font-size: clamp(2.1rem, 4.6vw, 3.6rem);
  line-height: 1.0;
  letter-spacing: -0.032em;
  margin: 0;
  color: var(--ink);

  .accent {
    font-family: var(--serif);
    font-weight: 400;
    font-style: italic;
    letter-spacing: -0.012em;
    color: var(--accent);
  }
`;

const Sub = styled.p`
  font-family: var(--sans);
  font-size: 1.05rem;
  line-height: 1.55;
  color: var(--ink-2);
  margin: 1.2rem auto 0;
  max-width: 56ch;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;

  @media (min-width: 720px) {
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
  }
`;

const Card = styled(motion.article)`
  background: var(--bg);
  border: 1px solid var(--rule);
  border-radius: 18px;
  padding: 2rem 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  box-shadow: var(--shadow-card);
  transition: border-color 280ms ease, box-shadow 320ms ease;

  &:hover {
    border-color: var(--rule-2);
    box-shadow: var(--shadow-card-hover);
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const IconCircle = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: var(--bg-2);
  border: 1px solid var(--rule);
  display: grid;
  place-items: center;
  font-size: 1.5rem;
  flex-shrink: 0;
`;

const Name = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  .t {
    font-family: var(--sans);
    font-weight: 600;
    font-size: 1.25rem;
    letter-spacing: -0.015em;
    color: var(--ink);
    line-height: 1.1;
  }

  .k {
    font-family: var(--mono);
    font-size: 10px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--ink-3);
  }
`;

const Services = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

const Service = styled.li`
  display: grid;
  grid-template-columns: 16px 1fr;
  align-items: baseline;
  gap: 10px;
  font-family: var(--sans);
  font-size: 0.95rem;
  line-height: 1.45;
  color: var(--ink-2);

  &::before {
    content: "";
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--accent);
    align-self: center;
    margin-left: 5px;
  }
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--rule);
`;

const Tag = styled.span`
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-2);
  background: var(--bg-2);
  border: 1px solid var(--rule);
  padding: 5px 9px;
  border-radius: 999px;
`;

const reveal = (i) => ({
  initial: { opacity: 0, y: 22 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.55, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
});

export default function Industries() {
  return (
    <Wrap id="industrias">
      <Inner>
        <Head>
          <Eyebrow>Industrias</Eyebrow>
          <Title>
            Especial para productos <span className="accent">que se acaban.</span>
          </Title>
          <Sub>
            Nos enfocamos en industrias de productos y servicios recurrentes que
            necesitan diferenciarse y tomar mejores decisiones con datos.
          </Sub>
        </Head>

        <Grid>
          <Card {...reveal(0)}>
            <Header>
              <IconCircle>🐶</IconCircle>
              <Name>
                <span className="t">Mascotas</span>
                <span className="k">Recurrencia alta</span>
              </Name>
            </Header>

            <Services>
              <Service>Recordatorios de alimentos y snacks</Service>
              <Service>Vacunas, medicamentos y antiparasitarios</Service>
              <Service>Crosselling de snacks y accesorios</Service>
              <Service>Personalización con nombre de mascotas</Service>
            </Services>

            <Tags>
              <Tag>Alimentos</Tag>
              <Tag>Veterinaria</Tag>
              <Tag>Snacks</Tag>
            </Tags>
          </Card>

          <Card {...reveal(1)}>
            <Header>
              <IconCircle>🌿</IconCircle>
              <Name>
                <span className="t">Bienestar</span>
                <span className="k">Tratamientos continuos</span>
              </Name>
            </Header>

            <Services>
              <Service>Recompra de suplementos y vitaminas</Service>
              <Service>Recordatorios de medicamentos</Service>
              <Service>Renovación de tratamientos y terapias</Service>
              <Service>Seguimiento de rutinas mensuales</Service>
            </Services>

            <Tags>
              <Tag>Suplementos</Tag>
              <Tag>Medicamentos</Tag>
              <Tag>Terapias</Tag>
              <Tag>Tratamientos</Tag>
            </Tags>
          </Card>
        </Grid>
      </Inner>
    </Wrap>
  );
}
